import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Mail, Trash2, Plus, AlertCircle, MoreVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "@core/hooks/use-toast";
import { Skeleton } from "@core/components/ui/skeleton";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@core/components/ui/sheet";
import {
  listPollInvitations,
  revokePollInvitation,
  deletePollInvitation,
  sendInvitationReminders,
  isInReminderCooldown,
  createPollInvitations,
  getPollGroupSyncStatus,
  type InvitationRow,
  type GroupSyncStatus,
} from "@core/services/InvitationService";
import { SaveEmailsAsGroupCta } from "@core/components/groups/SaveEmailsAsGroupCta";
import { AddInviteesDialog } from "@core/components/invitations/AddInviteesDialog";
import { AutoRemindersToggle } from "@core/components/invitations/AutoRemindersToggle";
import {
  getPollAutoReminders,
  setPollAutoReminders,
} from "@core/services/InvitationService";
import { useAuth } from "@core/contexts/AuthContext";
import { hasPaidAccess } from "@core/constants/quotas";

interface PollInvitationListProps {
  pollId: string;
  onInvitationSent?: (count: number) => void;
  /**
   * Sondage sans aucun invité : afficher un état vide (explication, bouton
   * d'ajout, relance automatique) au lieu de ne rien rendre.
   *
   * `false` par défaut — dans ProductSettings, ce composant est un panneau
   * parmi d'autres et une carte vide ne ferait qu'encombrer. La page dédiée
   * /poll/:slug/invitations, elle, n'a que ça à montrer : sans état vide,
   * elle restait entièrement blanche.
   */
  showWhenEmpty?: boolean;
}

type FilterType = "all" | "voted" | "waiting" | "revoked";

export const PollInvitationList: React.FC<PollInvitationListProps> = ({
  pollId,
  onInvitationSent,
  showWhenEmpty = false,
}) => {
  const { t } = useTranslation("polls");
  const { toast } = useToast();
  const { user, profile } = useAuth();
  // Verrouillage visuel seulement : la vérification qui compte est dans
  // set_poll_auto_reminders (propriétaire + abonnement Solo produit date).
  const autoRemindersLocked = !user || !hasPaidAccess(profile);

  const [rows, setRows] = useState<InvitationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>("all");
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(10);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [resendingAll, setResendingAll] = useState(false);
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<GroupSyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [autoReminders, setAutoReminders] = useState(false);
  const [savingAutoReminders, setSavingAutoReminders] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getPollAutoReminders(pollId).then((enabled) => {
      if (!cancelled) setAutoReminders(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, [pollId]);

  const handleAutoRemindersChange = useCallback(
    async (next: boolean) => {
      setSavingAutoReminders(true);
      // Optimiste : remis en arrière si la base refuse.
      setAutoReminders(next);
      try {
        await setPollAutoReminders(pollId, next);
        toast({
          title: next
            ? t("invitations.auto_reminders.enabled_toast")
            : t("invitations.auto_reminders.disabled_toast"),
        });
      } catch (err) {
        setAutoReminders(!next);
        const message = err instanceof Error ? err.message : String(err);
        toast({
          title: message.includes("SOLO_PLAN_REQUIRED")
            ? t("invitations.auto_reminders.solo_required")
            : t("invitations.auto_reminders.save_error"),
          variant: "destructive",
        });
      } finally {
        setSavingAutoReminders(false);
      }
    },
    [pollId, t, toast],
  );

  const loadInvitations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listPollInvitations(pollId);
      setRows(data);
    } catch (err) {
      setError((err as Error).message);
      console.error("Failed to load invitations:", err);
    } finally {
      setLoading(false);
    }
  }, [pollId]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const status = await getPollGroupSyncStatus(pollId);
      setSyncStatus(status);
    } catch (err) {
      // Non bloquant : le bandeau est une amélioration, pas le cœur de la
      // liste. Une erreur ici masque juste le bandeau.
      console.error("Failed to load group sync status:", err);
    }
  }, [pollId]);

  useEffect(() => {
    loadInvitations();
    loadSyncStatus();
  }, [loadInvitations, loadSyncStatus]);

  const handleSyncGroup = async () => {
    if (!syncStatus) return;
    setSyncing(true);
    try {
      await createPollInvitations(pollId, syncStatus.new_emails, {
        source: "group",
        groupId: syncStatus.group_id,
      });
      toast({
        title: t("invitations.tracking.sync_group_done", {
          count: syncStatus.new_emails.length,
        }),
      });
      setSyncStatus(null);
      await loadInvitations();
    } catch (err) {
      toast({
        title: t("invitations.error_title"),
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setSyncing(false);
    }
  };

  // Filtrer et rechercher
  const filtered = rows.filter((row) => {
    // Filtre par statut
    if (filter === "voted" && !row.has_voted) return false;
    if (filter === "waiting" && !["pending", "sent"].includes(row.status)) {
      return false;
    }
    if (filter === "revoked" && row.status !== "revoked") return false;

    // Recherche par email
    if (query && !row.email.toLowerCase().includes(query.toLowerCase())) {
      return false;
    }

    return true;
  });

  const displayed = filtered.slice(0, shown);
  const hasMore = shown < filtered.length;

  // Compter les statuts
  const counts = {
    all: rows.length,
    voted: rows.filter((r) => r.has_voted).length,
    waiting: rows.filter((r) => ["pending", "sent"].includes(r.status)).length,
    revoked: rows.filter((r) => r.status === "revoked").length,
  };

  // Provenance mixte : n'afficher l'étiquette par ligne que si l'audience
  // mélange groupe et saisie manuelle (cf. spec Écran 1 §5).
  const hasMixedSource = useMemo(
    () =>
      rows.some((r) => r.source === "group") &&
      rows.some((r) => r.source === "manual"),
    [rows],
  );

  // Emails saisis à la main (pas déjà issus d'un groupe) : matière première
  // du CTA "sauvegarder comme groupe réutilisable".
  const manualEmails = useMemo(
    () =>
      rows
        .filter((r) => r.source !== "group" && r.status !== "revoked")
        .map((r) => r.email),
    [rows],
  );

  const sheetRow = sheetId ? rows.find((r) => r.id === sheetId) : null;

  // Relançables : en attente (pas voté, pas révoqué) et pas de mail depuis
  // 24 h. Même règle que le serveur, qui reste seul juge.
  const canRemind = (r: InvitationRow) =>
    !r.has_voted && (r.status === "pending" || r.status === "sent");
  const remindable = rows.filter(
    (r) => canRemind(r) && !isInReminderCooldown(r),
  );

  // Actions — relances réelles (mail parti, même lien). L'état des lignes est
  // relu depuis le serveur après coup : c'est lui qui décide ce qui est parti.
  const handleResend = async (invitationId: string, email: string) => {
    setActionInProgress(invitationId);
    try {
      const res = await sendInvitationReminders(pollId, [invitationId]);
      if (res.successCount > 0) {
        toast({ title: t("invitations.tracking.action_resent", { email }) });
        onInvitationSent?.(1);
      } else if (res.skipped.cooldown > 0) {
        toast({ title: t("invitations.tracking.resend_cooldown") });
      } else {
        toast({
          title: t("invitations.error_title"),
          description: t("invitations.tracking.action_resent_failed", {
            email,
          }),
          variant: "destructive",
        });
      }
      await loadInvitations();
    } finally {
      setActionInProgress(null);
      setSheetId(null);
    }
  };

  const handleResendAll = async () => {
    const targets = remindable.map((r) => r.id);
    if (targets.length === 0) return;
    setResendingAll(true);
    try {
      const res = await sendInvitationReminders(pollId, targets);
      if (res.successCount > 0) {
        toast({
          title: t("invitations.tracking.action_resent_bulk", {
            count: res.successCount,
          }),
        });
        onInvitationSent?.(res.successCount);
      }
      if (res.failedCount > 0) {
        toast({
          title: t("invitations.error_title"),
          description: t("invitations.tracking.action_resent_bulk_error", {
            count: res.failedCount,
          }),
          variant: "destructive",
        });
      }
      if (res.skipped.cooldown > 0) {
        toast({
          title: t("invitations.tracking.action_resent_bulk_skipped", {
            count: res.skipped.cooldown,
          }),
        });
      }
      await loadInvitations();
    } finally {
      setResendingAll(false);
    }
  };

  const handleRevoke = async (invitationId: string, email: string) => {
    setActionInProgress(invitationId);
    try {
      await revokePollInvitation(invitationId, pollId);
      setRows((prev) =>
        prev.map((r) =>
          r.id === invitationId
            ? { ...r, status: "revoked", revoked_at: new Date().toISOString() }
            : r,
        ),
      );
      toast({
        title: t("invitations.tracking.action_revoked", {
          email,
        }),
      });
    } catch (err) {
      toast({
        title: t("invitations.error_title"),
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setActionInProgress(null);
      setSheetId(null);
    }
  };

  const handleDelete = async (invitationId: string, email: string) => {
    setActionInProgress(invitationId);
    try {
      await deletePollInvitation(invitationId, pollId);
      setRows((prev) => prev.filter((r) => r.id !== invitationId));
      setConfirmingId(null);
      toast({
        title: t("invitations.tracking.action_deleted", {
          email,
        }),
      });
    } catch (err) {
      toast({
        title: t("invitations.error_title"),
        description: (err as Error).message,
        variant: "destructive",
      });
    } finally {
      setActionInProgress(null);
      setSheetId(null);
    }
  };

  // Retour au début à chaque changement de filtre
  useEffect(() => {
    setShown(10);
    setConfirmingId(null);
  }, [filter, query]);

  // Rendu
  if (rows.length === 0 && !loading && !error) {
    if (!showWhenEmpty) {
      return null; // Panneau intégré : ne rien encombrer
    }

    return (
      <div
        className="bg-card rounded-lg border border-border max-w-[820px] p-5 space-y-4"
        data-testid="invitations-empty-state"
      >
        <div className="flex gap-3">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-lg shrink-0">
            <Mail className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-foreground">
              {t("invitations.tracking.panel_title")}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              {t("invitations.tracking.empty_description")}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setAddOpen(true)}
          data-testid="invitations-add-guests-empty"
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded"
        >
          <Plus className="w-3.5 h-3.5" />
          {t("invitations.tracking.add_guests")}
        </button>

        {/* Réglable avant même d'inviter : un créateur Solo peut décider une
            fois pour toutes, sans attendre d'avoir ajouté quelqu'un. */}
        <AutoRemindersToggle
          checked={autoReminders}
          onChange={handleAutoRemindersChange}
          locked={autoRemindersLocked}
          busy={savingAutoReminders}
        />

        <AddInviteesDialog
          pollId={pollId}
          open={addOpen}
          onOpenChange={setAddOpen}
          onAdded={(count) => {
            onInvitationSent?.(count);
            void loadInvitations();
            void loadSyncStatus();
          }}
        />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden max-w-[820px]">
      {/* Header */}
      <div className="flex gap-3 p-5 border-b border-border">
        <div className="flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-lg">
          <Mail className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {t("invitations.tracking.panel_title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            {t("invitations.tracking.panel_subtitle")}
          </p>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-destructive-bg border-b border-destructive flex gap-3">
          <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-foreground font-medium">
              {t("invitations.tracking.error_load")}
            </p>
            <button
              onClick={loadInvitations}
              className="text-xs text-destructive underline hover:text-destructive/80 mt-1"
            >
              {t("invitations.tracking.error_retry")}
            </button>
          </div>
        </div>
      )}

      {/* Bandeau de synchronisation de groupe */}
      {syncStatus && (
        <div className="p-4 bg-primary/10 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-primary flex-1 min-w-[200px]">
            <span className="font-semibold">
              {t("invitations.tracking.sync_new_members", {
                count: syncStatus.new_emails.length,
                group: syncStatus.group_name,
              })}
            </span>
          </p>
          <button
            onClick={handleSyncGroup}
            disabled={syncing}
            data-testid="invitations-sync-group"
            className="shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap"
          >
            {t("invitations.tracking.sync_group", {
              count: syncStatus.new_emails.length,
            })}
          </button>
        </div>
      )}

      {/* Filtres */}
      <div className="flex gap-2 p-4 bg-muted border-b border-border overflow-x-auto">
        {(
          [
            {
              key: "all",
              label: t("invitations.tracking.filter_all"),
              count: counts.all,
            },
            {
              key: "voted",
              label: t("invitations.tracking.filter_voted"),
              count: counts.voted,
            },
            {
              key: "waiting",
              label: t("invitations.tracking.filter_waiting"),
              count: counts.waiting,
            },
            {
              key: "revoked",
              label: t("invitations.tracking.filter_revoked"),
              count: counts.revoked,
            },
          ] as Array<{ key: FilterType; label: string; count: number }>
        ).map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            aria-pressed={filter === key}
            className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
              filter === key
                ? "bg-primary text-primary-foreground"
                : "bg-card border border-border text-foreground hover:bg-accent"
            }`}
          >
            {count} {label.charAt(0).toLowerCase() + label.slice(1)}
          </button>
        ))}
      </div>

      {/* Relance automatique (Solo) : l'automatisation est la contrepartie
          payante ; la relance manuelle ci-dessous reste gratuite. */}
      <div className="p-4 border-b border-border">
        <AutoRemindersToggle
          checked={autoReminders}
          onChange={handleAutoRemindersChange}
          locked={autoRemindersLocked}
          busy={savingAutoReminders}
        />
      </div>

      {/* Recherche + relance groupée */}
      <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          placeholder={t("invitations.tracking.search_placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <button
          onClick={handleResendAll}
          disabled={remindable.length === 0 || resendingAll}
          data-testid="invitations-resend-all"
          className="shrink-0 px-3 py-2 text-xs font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {remindable.length > 0
            ? t("invitations.tracking.resend_all", {
                count: remindable.length,
              })
            : rows.some(canRemind)
              ? t("invitations.tracking.resend_all_cooldown")
              : t("invitations.tracking.resend_all_disabled")}
        </button>
      </div>

      {/* Liste */}
      <div className="max-h-[440px] overflow-y-auto">
        {loading ? (
          <div className="divide-y divide-border">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="p-4 grid grid-cols-[1fr_auto] gap-3">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <p className="text-sm">
              {t("invitations.tracking.empty_filtered")}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {displayed.map((row) => (
              <div key={row.id}>
                {/* Ligne d'invitation */}
                <div className="p-4 grid grid-cols-[1fr_auto] gap-3 hover:bg-accent">
                  <div>
                    <div className="flex flex-wrap gap-2 items-start mb-1">
                      <span className="text-sm font-medium text-foreground break-all">
                        {row.email}
                      </span>
                      <StatusBadge
                        status={row.status}
                        hasVoted={row.has_voted}
                      />
                    </div>
                    {hasMixedSource && (
                      <div className="text-[11px] font-medium text-muted-foreground mb-1">
                        {row.source === "group"
                          ? t("invitations.tracking.source_group")
                          : t("invitations.tracking.source_manual")}
                      </div>
                    )}
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      {row.last_sent_at && (
                        <span>
                          {/* « Relancé le » seulement s'il y a eu une relance :
                              au premier envoi, send_count = 1. */}
                          {t(
                            row.send_count > 1
                              ? "invitations.tracking.last_sent_date"
                              : "invitations.tracking.first_sent_date",
                            {
                              date: new Date(
                                row.last_sent_at,
                              ).toLocaleDateString("fr-FR"),
                            },
                          )}
                        </span>
                      )}
                      {row.send_count > 1 && (
                        <span>
                          {t("invitations.tracking.resend_count", {
                            count: row.send_count - 1,
                          })}
                        </span>
                      )}
                      {row.first_used_at ? (
                        <span>
                          {t("invitations.tracking.first_access", {
                            date: new Date(
                              row.first_used_at,
                            ).toLocaleDateString("fr-FR"),
                            time: new Date(
                              row.first_used_at,
                            ).toLocaleTimeString("fr-FR", {
                              hour: "2-digit",
                              minute: "2-digit",
                            }),
                          })}
                        </span>
                      ) : (
                        <span>{t("invitations.tracking.never_opened")}</span>
                      )}
                      {row.status !== "revoked" && row.expires_at && (
                        <span>
                          {t("invitations.tracking.expires_date", {
                            date: new Date(row.expires_at).toLocaleDateString(
                              "fr-FR",
                            ),
                          })}
                        </span>
                      )}
                      {row.status === "revoked" && (
                        <span>{t("invitations.tracking.link_disabled")}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions desktop : inline. Mobile (<640px) : menu "⋯" -> bottom sheet. */}
                  <div className="hidden sm:flex gap-1">
                    {canRemind(row) && (
                      <button
                        onClick={() => handleResend(row.id, row.email)}
                        disabled={
                          actionInProgress === row.id ||
                          isInReminderCooldown(row)
                        }
                        title={
                          isInReminderCooldown(row)
                            ? t("invitations.tracking.resend_cooldown")
                            : undefined
                        }
                        className="px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {row.status === "pending" && !row.sent_at
                          ? t("invitations.tracking.send_one")
                          : t("invitations.tracking.resend_one")}
                      </button>
                    )}
                    {row.status !== "revoked" && (
                      <button
                        onClick={() => handleRevoke(row.id, row.email)}
                        disabled={actionInProgress === row.id}
                        className="px-2 py-1 text-xs font-medium text-foreground hover:bg-accent rounded disabled:opacity-50"
                      >
                        {t("invitations.tracking.revoke")}
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmingId(row.id)}
                      disabled={actionInProgress === row.id}
                      aria-label={t("invitations.tracking.delete_button")}
                      className="px-2 py-1 text-xs font-medium text-foreground hover:bg-accent rounded disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    onClick={() => setSheetId(row.id)}
                    disabled={actionInProgress === row.id}
                    aria-label={t("invitations.tracking.actions_menu")}
                    data-testid="invitations-row-menu"
                    className="sm:hidden flex items-center justify-center w-11 h-11 -m-2 text-foreground hover:bg-accent rounded disabled:opacity-50"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>

                {/* Confirmation de suppression (desktop inline) */}
                {confirmingId === row.id && (
                  <div className="p-3 bg-destructive-bg border-t border-destructive flex gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {t("invitations.tracking.delete_confirm")}
                      </p>
                      <p className="text-xs text-destructive mt-1">
                        {t("invitations.tracking.delete_confirm_desc")}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setConfirmingId(null)}
                        className="px-3 py-1 text-xs font-medium text-foreground hover:bg-accent rounded"
                      >
                        {t("invitations.tracking.delete_cancel")}
                      </button>
                      <button
                        onClick={() => handleDelete(row.id, row.email)}
                        disabled={actionInProgress === row.id}
                        className="px-3 py-1 text-xs font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 rounded disabled:opacity-50"
                      >
                        {t("invitations.tracking.delete_button")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {hasMore && (
        <div className="p-3 border-t border-border">
          <button
            onClick={() => setShown((s) => s + 10)}
            className="w-full px-3 py-2 text-xs font-medium text-foreground hover:bg-accent rounded"
          >
            {t("invitations.tracking.load_more", {
              count: Math.min(10, filtered.length - shown),
              remaining: filtered.length - shown,
            })}
          </button>
        </div>
      )}

      {/* Pied */}
      <div className="p-3 bg-muted border-t border-border flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">
          {t("invitations.tracking.showing", {
            shown: displayed.length,
            total: filtered.length,
          })}
        </span>
        <div className="flex items-center gap-3">
          {manualEmails.length > 0 && (
            <SaveEmailsAsGroupCta emails={manualEmails} />
          )}
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="invitations-add-guests"
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 rounded"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("invitations.tracking.add_guests")}
          </button>
        </div>
      </div>

      <AddInviteesDialog
        pollId={pollId}
        open={addOpen}
        onOpenChange={setAddOpen}
        onAdded={(count) => {
          onInvitationSent?.(count);
          void loadInvitations();
          void loadSyncStatus();
        }}
      />

      {/* Feuille d'actions mobile */}
      <Sheet
        open={!!sheetRow}
        onOpenChange={(open) => {
          if (!open) {
            setSheetId(null);
            setConfirmingId(null);
          }
        }}
      >
        <SheetContent side="bottom" className="rounded-t-2xl">
          {sheetRow && (
            <>
              <SheetHeader className="mb-2">
                <SheetTitle className="flex flex-wrap items-center gap-2 text-left break-all">
                  {sheetRow.email}
                  <StatusBadge
                    status={sheetRow.status}
                    hasVoted={sheetRow.has_voted}
                  />
                </SheetTitle>
              </SheetHeader>
              {confirmingId === sheetRow.id ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    {t("invitations.tracking.delete_confirm")}
                  </p>
                  <p className="text-xs text-destructive">
                    {t("invitations.tracking.delete_confirm_desc")}
                  </p>
                  <button
                    onClick={() => handleDelete(sheetRow.id, sheetRow.email)}
                    disabled={actionInProgress === sheetRow.id}
                    className="w-full min-h-11 px-4 text-sm font-medium rounded-lg text-destructive-foreground bg-destructive hover:bg-destructive/90 disabled:opacity-50"
                  >
                    {t("invitations.tracking.delete_button")}
                  </button>
                  <button
                    onClick={() => setConfirmingId(null)}
                    className="w-full min-h-11 px-4 text-sm font-medium rounded-lg text-foreground hover:bg-accent"
                  >
                    {t("invitations.tracking.delete_cancel")}
                  </button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {canRemind(sheetRow) && (
                    <button
                      onClick={() => handleResend(sheetRow.id, sheetRow.email)}
                      disabled={
                        actionInProgress === sheetRow.id ||
                        isInReminderCooldown(sheetRow)
                      }
                      className="w-full min-h-11 px-4 text-sm font-medium rounded-lg text-primary-foreground bg-primary hover:bg-primary/90 disabled:opacity-50"
                    >
                      {isInReminderCooldown(sheetRow)
                        ? t("invitations.tracking.resend_cooldown")
                        : sheetRow.status === "pending" && !sheetRow.sent_at
                          ? t("invitations.tracking.send_one")
                          : t("invitations.tracking.resend_one")}
                    </button>
                  )}
                  {sheetRow.status !== "revoked" && (
                    <button
                      onClick={() => handleRevoke(sheetRow.id, sheetRow.email)}
                      disabled={actionInProgress === sheetRow.id}
                      className="w-full min-h-11 px-4 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-accent disabled:opacity-50"
                    >
                      {t("invitations.tracking.revoke")}
                    </button>
                  )}
                  <button
                    onClick={() => setConfirmingId(sheetRow.id)}
                    disabled={actionInProgress === sheetRow.id}
                    className="w-full min-h-11 px-4 text-sm font-medium rounded-lg text-destructive hover:bg-destructive-bg disabled:opacity-50"
                  >
                    {t("invitations.tracking.delete_button")}
                  </button>
                  <button
                    onClick={() => setSheetId(null)}
                    className="w-full min-h-11 px-4 text-sm font-medium rounded-lg text-foreground hover:bg-accent"
                  >
                    {t("common:close")}
                  </button>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

function StatusBadge({
  status,
  hasVoted,
}: {
  status: string;
  hasVoted: boolean;
}) {
  const { t } = useTranslation("polls");

  let bgClass = "bg-muted";
  let textClass = "text-muted-foreground";
  let text = status;

  if (hasVoted || status === "voted") {
    bgClass = "bg-success-bg";
    textClass = "text-success";
    text = t("invitations.tracking.status_voted");
  } else if (status === "sent") {
    bgClass = "bg-warning-bg";
    textClass = "text-warning";
    text = t("invitations.tracking.status_sent");
  } else if (status === "pending") {
    bgClass = "bg-warning-bg";
    textClass = "text-warning";
    text = t("invitations.tracking.status_pending");
  } else if (status === "revoked") {
    bgClass = "bg-muted";
    textClass = "text-muted-foreground";
    text = t("invitations.tracking.status_revoked");
  }

  return (
    <span
      className={`px-2 py-0.5 text-xs font-medium rounded-full ${bgClass} ${textClass}`}
    >
      {text}
    </span>
  );
}
